import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { IBreadcrumb, IBreadcrumbInput, IBreadcrumbRouteInput } from '@app/models/beadcrumb/breadcrumb.model';
import { BehaviorSubject, Subject, filter, map, mergeMap } from 'rxjs';

export const RouteTrackerState = {
    showBackButton$: new BehaviorSubject<boolean>(false),
    breadcrumbs$: new BehaviorSubject<ReadonlyArray<IBreadcrumb>>([]),
    lastBreadcrumChange$: new Subject<IBreadcrumb>(),
    goBack$: new Subject<void>(),
    /**
     * Record names for the trail, keyed by the record's id (the last part of a crumb's address): a page that has
     * already loaded a parent record's name registers it here, and the crumb for that record shows the name
     * instead of the generic term ("Programmes / Summer Camp" rather than "Programmes / Programme").
     * Filled from data the page loads anyway - no extra requests.
     */
    recordNames$: new BehaviorSubject<Readonly<Record<string, string>>>({}),
};

/** Registers a record's name for the breadcrumb trail (see RouteTrackerState.recordNames$). */
export const nameBreadcrumbRecord = (recordId: string | null | undefined, name: string | null | undefined): void => {
    const id = (recordId ?? '').trim().toLowerCase();
    const label = (name ?? '').trim();
    if (!id || !label || RouteTrackerState.recordNames$.value[id] === label) return;
    RouteTrackerState.recordNames$.next({ ...RouteTrackerState.recordNames$.value, [id]: label });
};

const prepareRouteUrl = (baseUrl: string, appendUrl?: string, params?: { [key: string]: string }): string => {

    if (appendUrl) {
        baseUrl += '/' + appendUrl;
    }
    if (params && baseUrl) {
        Object.keys(params).forEach(key => {
            baseUrl = baseUrl.replace(new RegExp(`:${key}`, "g"), params[key]);
        });
    }
    return baseUrl;
}

const toBreadcrumbInputs = (breadcrumbRouteInput?: IBreadcrumbRouteInput): ReadonlyArray<IBreadcrumbInput> => {
    if (Array.isArray(breadcrumbRouteInput)) {
        return breadcrumbRouteInput;
    } else {
        return [breadcrumbRouteInput as IBreadcrumbInput];
    }

}

const apendBreacrumb = (breadcrumbs: IBreadcrumb[], routeUrl: string, routeSnapshot: ActivatedRouteSnapshot): IBreadcrumb[] => {
    const bredcrumbRouteInput: IBreadcrumbRouteInput | undefined = routeSnapshot.routeConfig.data?.bredcrumbInput;
    if (bredcrumbRouteInput) {
        const bredcrumbInputs: ReadonlyArray<IBreadcrumbInput> = toBreadcrumbInputs(bredcrumbRouteInput);
        bredcrumbInputs.forEach(({ label, termKey, labelSuffix }) => {
            if (label || termKey) {
                const breadcrumb: IBreadcrumb = { label: label ?? '', termKey, labelSuffix, link: routeUrl };
                breadcrumbs.push(breadcrumb);
            }
        })
    }
    return [...breadcrumbs];
}

export const enableRouteTracker = (router: Router, activatedRoute: ActivatedRoute) => {
    let routeUrl = '';
    let breadcrumbs: IBreadcrumb[] = []
    return router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => activatedRoute),
        map((route: ActivatedRoute) => {
            breadcrumbs = [];
            routeUrl = '';
            while (route.firstChild) {
                route = route.firstChild;

                const routePath = route.snapshot.routeConfig.path;
                const params = route.snapshot.params;
                routeUrl = prepareRouteUrl(routeUrl, routePath, params);
                breadcrumbs = apendBreacrumb(breadcrumbs, routeUrl, route.snapshot)
            }
            return route;
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
    ).subscribe((data: any) => {
        if (data?.showBackButton) {
            RouteTrackerState.showBackButton$.next(data.showBackButton);
        } else {
            RouteTrackerState.showBackButton$.next(false);
        }
        RouteTrackerState.breadcrumbs$.next(breadcrumbs);
    });
};


RouteTrackerState.lastBreadcrumChange$.subscribe((lastBreadcrum: IBreadcrumb) => {
    if (lastBreadcrum) {
        const breadcrumbs = [...RouteTrackerState.breadcrumbs$.value];
        breadcrumbs.pop();
        breadcrumbs.push(lastBreadcrum);
        RouteTrackerState.breadcrumbs$.next(breadcrumbs);
    }
})